import { isAvailableNow } from "@/lib/is-available-now"
import type { Availability, Category, ItemOffer } from "@/types"

/** Clock overrides for availability checks: reactive callers pass the tenant zone and tick. */
export type AvailabilityClock = { now?: Date; timeZone?: string | null }

/** Why an offer cannot be ordered right now; `null` from `explainOfferUnavailability` means it can. */
export type OfferUnavailabilityReason =
  | "offer_unavailable"
  | "offer_out_of_schedule"
  | "category_unavailable"
  | "category_out_of_schedule"

/**
 * Single source of truth for "can this offer be ordered right now?".
 *
 * An offer is available only when BOTH signals allow it:
 *   1. the status flags (`AVAILABLE` vs `UNAVAILABLE`) on the offer and, when
 *      provided, its owning category — the manual out-of-stock toggle;
 *   2. the scheduled-hours window (`availabilityId` → `availabilities`),
 *      evaluated in UTC for the offer and, when provided, its category.
 *
 * Passing the `category` lets callers enforce category-level scheduling (a whole
 * section can be time-gated). It is optional so callers that only hold the offer
 * (e.g. a cart line) still get the offer-level guard.
 */
export function isOfferAvailableNow(
  offer: Pick<ItemOffer, "status" | "availabilityId">,
  availabilities: Availability[],
  category?: Pick<Category, "status" | "availabilityId"> | null,
  clock: AvailabilityClock = {},
): boolean {
  return explainOfferUnavailability(offer, availabilities, category, clock) === null
}

/** The first signal that takes the offer off the shelf, in the order `isOfferAvailableNow` checks them. */
export function explainOfferUnavailability(
  offer: Pick<ItemOffer, "status" | "availabilityId">,
  availabilities: Availability[],
  category?: Pick<Category, "status" | "availabilityId"> | null,
  clock: AvailabilityClock = {},
): OfferUnavailabilityReason | null {
  if (offer.status === "UNAVAILABLE") return "offer_unavailable"
  if (!isAvailableNow(offer.availabilityId, availabilities, clock.now, clock.timeZone)) return "offer_out_of_schedule"

  if (category) {
    if (category.status === "UNAVAILABLE") return "category_unavailable"
    if (!isAvailableNow(category.availabilityId, availabilities, clock.now, clock.timeZone)) return "category_out_of_schedule"
  }

  return null
}
